import React, { useEffect, useMemo, useState } from 'react';
import jsPDF from 'jspdf';

const UXO_INITIAL_FORM = {
  line1: '',
  line2: '',
  line3: '',
  line4: '',
  line5: '',
  line6: '',
  line7: '',
  line8: '',
  line9: '',
};

const PERSONNEL_INJURY_TEMPLATE_FALLBACK = {
  classification: 'CUI',
  maintenanceGroup: '',
  checklistNumber: '#505',
  checklistTitle: 'PERSONNEL INJURY',
  updatedLabel: '',
  checklistRows: [],
};

function cloneChecklistRows(rows) {
  return rows.map(row => ({ ...row }));
}

function nowLocalInputValue() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function toLocalInputValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function formatDateTimeDisplay(value) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return `${date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`;
}

function buildPersonnelOptions(personnelData) {
  return (personnelData || []).map(person => {
    const firstName = person['First Name'] || '';
    const lastName = person['Last Name'] || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return {
      id: person.id,
      fullName,
      empNum: person['Emp Num'] || '',
      rank: person.Rank || '',
      workcenter: person['Shop Name'] || '',
      email: person.Email || '',
      display: [fullName, person['Emp Num'], person.Rank, person['Shop Name']].filter(Boolean).join(' - '),
    };
  });
}

function findRosterPersonForUser(personnelData, userEmail) {
  const normalizedEmail = (userEmail || '').trim().toLowerCase();
  if (!normalizedEmail) return null;
  return (personnelData || []).find(person => (
    String(person.Email || '').trim().toLowerCase() === normalizedEmail
  )) || null;
}

function createDefaultInjuryForm(template = PERSONNEL_INJURY_TEMPLATE_FALLBACK) {
  return {
    id: '',
    classification: template.classification || 'CUI',
    maintenanceGroup: template.maintenanceGroup || '',
    checklistNumber: template.checklistNumber || '#505',
    checklistTitle: template.checklistTitle || 'PERSONNEL INJURY',
    updatedLabel: template.updatedLabel || '',
    controllerName: '',
    controllerEmpNum: '',
    controllerPersonnelId: '',
    notifiedByName: '',
    notifiedByEmpNum: '',
    notifiedByPersonnelId: '',
    injuredName: '',
    injuredEmpNum: '',
    injuredPersonnelId: '',
    injuredRank: '',
    injuredWorkcenter: '',
    injured2Name: '',
    injured2EmpNum: '',
    injured2PersonnelId: '',
    injured2Rank: '',
    injured2Workcenter: '',
    reportDateTime: nowLocalInputValue(),
    locationAircraft: '',
    circumstances: '',
    injuryType: '',
    bodyPart: '',
    severity: 'Moderate',
    medicalCare: false,
    ambulanceRequired: false,
    called911: false,
    headInjury: false,
    commanderEscalationRequired: false,
    escortedHospitalRequired: false,
    notes: '',
    checklistRows: cloneChecklistRows(template.checklistRows || []),
    completedByName: '',
    completedAt: '',
  };
}

function applyInjuryReporterDefaults(form, rosterPerson) {
  if (!rosterPerson) return form;

  const fullName = `${rosterPerson['First Name'] || ''} ${rosterPerson['Last Name'] || ''}`.trim();
  const empNum = rosterPerson['Emp Num'] || '';
  const personnelId = rosterPerson.id || '';

  return {
    ...form,
    controllerName: form.controllerName || fullName,
    controllerEmpNum: form.controllerEmpNum || empNum,
    controllerPersonnelId: form.controllerPersonnelId || personnelId,
    notifiedByName: form.notifiedByName || fullName,
    notifiedByEmpNum: form.notifiedByEmpNum || empNum,
    notifiedByPersonnelId: form.notifiedByPersonnelId || personnelId,
  };
}

function PersonnelLookupAssist({
  title,
  prefix,
  form,
  setForm,
  personnelOptions,
  includeRank = false,
  includeWorkcenter = false,
}) {
  const [query, setQuery] = useState('');
  const matches = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return [];
    return personnelOptions
      .filter(option => (
        option.fullName.toLowerCase().includes(normalized) ||
        option.empNum.toLowerCase().includes(normalized)
      ))
      .slice(0, 8);
  }, [personnelOptions, query]);

  const applyMatch = (optionId) => {
    const option = personnelOptions.find(candidate => String(candidate.id) === String(optionId));
    if (!option) return;
    const rankField = `${prefix}Rank`;
    const workcenterField = `${prefix}Workcenter`;
    setForm(current => ({
      ...current,
      [`${prefix}Name`]: option.fullName,
      [`${prefix}EmpNum`]: option.empNum,
      [`${prefix}PersonnelId`]: option.id,
      ...(includeRank ? { [rankField]: option.rank || current[rankField] } : {}),
      ...(includeWorkcenter ? { [workcenterField]: option.workcenter || current[workcenterField] } : {}),
    }));
    setQuery('');
  };

  return (
    <div className="safety-lookup">
      <label>{title} Lookup</label>
      <input
        type="search"
        value={query}
        onChange={event => setQuery(event.target.value)}
        placeholder="Search by name or personnel #"
      />
      {matches.length > 0 && (
        <select value="" onChange={event => applyMatch(event.target.value)}>
          <option value="">Select roster match</option>
          {matches.map(option => (
            <option key={option.id} value={option.id}>{option.display}</option>
          ))}
        </select>
      )}
    </div>
  );
}

export default function SafetyFormsTab({ api, user, selectedBase, globalView = false }) {
  const [uxoForm, setUxoForm] = useState(UXO_INITIAL_FORM);
  const [injuryTemplate, setInjuryTemplate] = useState(PERSONNEL_INJURY_TEMPLATE_FALLBACK);
  const [injuryForm, setInjuryForm] = useState(() => createDefaultInjuryForm(PERSONNEL_INJURY_TEMPLATE_FALLBACK));
  const [uxoReports, setUxoReports] = useState([]);
  const [injuryReports, setInjuryReports] = useState([]);
  const [personnelData, setPersonnelData] = useState([]);
  const [uxoFilterDate, setUxoFilterDate] = useState('');
  const [uxoSearchTerm, setUxoSearchTerm] = useState('');
  const [injuryFilterDate, setInjuryFilterDate] = useState('');
  const [injurySearchTerm, setInjurySearchTerm] = useState('');
  const [uxoFeedback, setUxoFeedback] = useState('');
  const [injuryFeedback, setInjuryFeedback] = useState('');
  const [savingUxo, setSavingUxo] = useState(false);
  const [savingInjury, setSavingInjury] = useState(false);
  const [injuryTemplateFeedback, setInjuryTemplateFeedback] = useState('');
  const [savingInjuryTemplate, setSavingInjuryTemplate] = useState(false);

  const effectiveLocation = selectedBase || user?.location || '';
  const personnelOptions = useMemo(() => buildPersonnelOptions(personnelData), [personnelData]);
  const currentRosterPerson = useMemo(
    () => findRosterPersonForUser(personnelData, user?.email),
    [personnelData, user?.email]
  );
  const injuryChecklistMidpoint = Math.ceil(injuryForm.checklistRows.length / 2);
  const injuryChecklistLeft = injuryForm.checklistRows.slice(0, injuryChecklistMidpoint);
  const injuryChecklistRight = injuryForm.checklistRows.slice(injuryChecklistMidpoint);
  const canManageInjuryTemplate = Boolean(user?.is_superuser || ['Management', 'siteAdmin'].includes(user?.role || ''));
  const headInjuryEffective = useMemo(
    () => Boolean(injuryForm.headInjury || String(injuryForm.bodyPart || '').toLowerCase().includes('head')),
    [injuryForm.bodyPart, injuryForm.headInjury]
  );
  const medicalCareEffective = Boolean(injuryForm.medicalCare);

  const loadUxoReports = async () => {
    try {
      const res = await api.get('/get-uxo-reports', {
        params: {
          email: user.email,
          location: effectiveLocation,
          global: user.is_superuser && globalView ? true : undefined,
          date: uxoFilterDate || undefined,
          search: uxoSearchTerm || undefined,
        },
      });
      setUxoReports(res.data || []);
    } catch (err) {
      console.error('Failed to load UXO reports', err);
      setUxoReports([]);
      setUxoFeedback('Failed to load UXO reports.');
    }
  };

  const loadInjuryReports = async () => {
    try {
      const res = await api.get('/get-personnel-injury-reports', {
        params: {
          email: user.email,
          location: effectiveLocation,
          global: user.is_superuser && globalView ? true : undefined,
          date: injuryFilterDate || undefined,
          search: injurySearchTerm || undefined,
        },
      });
      setInjuryReports(res.data.data || []);
    } catch (err) {
      console.error('Failed to load personnel injury reports', err);
      setInjuryReports([]);
      setInjuryFeedback(err.response?.data?.message || 'Failed to load personnel injury reports.');
    }
  };

  const loadPersonnel = async () => {
    try {
      const res = await api.get('/get-personnel', {
        params: {
          email: user.email,
          location: effectiveLocation,
          global: user.is_superuser && globalView ? true : undefined,
        },
      });
      setPersonnelData(res.data.data || []);
    } catch (err) {
      console.error('Failed to load personnel for injury lookup', err);
    }
  };

  const loadInjuryTemplate = async () => {
    try {
      const res = await api.get('/safety-form-template', {
        params: {
          email: user.email,
          location: effectiveLocation,
          global: user.is_superuser && globalView ? true : undefined,
          reportType: 'personnel_injury',
        },
      });
      const nextTemplate = { ...PERSONNEL_INJURY_TEMPLATE_FALLBACK, ...(res.data.template || {}) };
      setInjuryTemplate(nextTemplate);
      setInjuryForm(current => (
        current.id
          ? current
          : applyInjuryReporterDefaults(createDefaultInjuryForm(nextTemplate), currentRosterPerson)
      ));
    } catch (err) {
      console.error('Failed to load personnel injury template', err);
      setInjuryTemplate(PERSONNEL_INJURY_TEMPLATE_FALLBACK);
      setInjuryForm(applyInjuryReporterDefaults(createDefaultInjuryForm(PERSONNEL_INJURY_TEMPLATE_FALLBACK), currentRosterPerson));
      setInjuryFeedback(err.response?.data?.message || 'Failed to load personnel injury base template.');
    }
  };

  useEffect(() => {
    setUxoReports([]);
    setInjuryReports([]);
    setPersonnelData([]);
    setUxoFeedback('');
    setInjuryFeedback('');
    setInjuryTemplate(PERSONNEL_INJURY_TEMPLATE_FALLBACK);
    setInjuryForm(createDefaultInjuryForm(PERSONNEL_INJURY_TEMPLATE_FALLBACK));
  }, [effectiveLocation]);

  useEffect(() => {
    loadUxoReports();
  }, [effectiveLocation, uxoFilterDate, uxoSearchTerm, globalView, user.email, user.is_superuser]);

  useEffect(() => {
    loadInjuryReports();
    loadPersonnel();
  }, [effectiveLocation, injuryFilterDate, injurySearchTerm, globalView, user.email, user.is_superuser]);

  useEffect(() => {
    loadInjuryTemplate();
  }, [effectiveLocation, globalView, user.email, user.is_superuser]);

  useEffect(() => {
    setInjuryForm(current => {
      if (current.id || current.controllerName || current.notifiedByName) {
        return current;
      }
      return applyInjuryReporterDefaults(current, currentRosterPerson);
    });
  }, [currentRosterPerson]);

  const updateInjuryChecklistRow = (index, field, value) => {
    setInjuryForm(current => ({
      ...current,
      checklistRows: current.checklistRows.map((row, rowIndex) => (
        rowIndex === index ? { ...row, [field]: value } : row
      )),
    }));
  };

  const addInjuryChecklistRow = () => {
    setInjuryForm(current => ({
      ...current,
      checklistRows: [
        ...current.checklistRows,
        { contact: '', phone: '', note: '', personNotified: '', time: '', method: '' },
      ],
    }));
  };

  const removeInjuryChecklistRow = (index) => {
    setInjuryForm(current => ({
      ...current,
      checklistRows: current.checklistRows.filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const handleSubmitUxo = async () => {
    setSavingUxo(true);
    try {
      await api.post('/submit-uxo-report', {
        ...uxoForm,
        email: user.email,
        location: effectiveLocation,
      });
      setUxoFeedback('UXO report submitted.');
      setUxoForm(UXO_INITIAL_FORM);
      await loadUxoReports();
    } catch (err) {
      console.error('Error submitting UXO report:', err);
      setUxoFeedback(err.response?.data?.message || 'Failed to submit UXO report.');
    } finally {
      setSavingUxo(false);
    }
  };

  const loadInjuryIntoForm = (report) => {
    setInjuryForm({
      ...applyInjuryReporterDefaults(createDefaultInjuryForm(injuryTemplate), currentRosterPerson),
      ...report,
      reportDateTime: toLocalInputValue(report.reportDateTime),
      completedAt: toLocalInputValue(report.completedAt),
      checklistRows: report.checklistRows?.length ? report.checklistRows.map(row => ({ ...row })) : cloneChecklistRows(injuryTemplate.checklistRows || []),
    });
    setInjuryFeedback('');
  };

  const resetInjuryForm = () => {
    setInjuryForm(applyInjuryReporterDefaults(createDefaultInjuryForm(injuryTemplate), currentRosterPerson));
    setInjuryFeedback('');
  };

  const saveInjuryReport = async () => {
    if (!injuryForm.injuredName.trim()) {
      setInjuryFeedback('Injured member name is required.');
      return;
    }
    if (!injuryForm.locationAircraft.trim()) {
      setInjuryFeedback('Location / aircraft is required.');
      return;
    }
    if (!injuryForm.circumstances.trim()) {
      setInjuryFeedback('Circumstances leading to injury are required.');
      return;
    }

    setSavingInjury(true);
    try {
      const payload = {
        ...injuryForm,
        email: user.email,
        location: effectiveLocation,
        headInjury: headInjuryEffective,
        commanderEscalationRequired: headInjuryEffective || injuryForm.commanderEscalationRequired,
        escortedHospitalRequired: headInjuryEffective || injuryForm.escortedHospitalRequired,
      };
      const res = await api.post('/save-personnel-injury-report', payload);
      setInjuryFeedback(res.data.message || 'Personnel injury report saved.');
      resetInjuryForm();
      await loadInjuryReports();
    } catch (err) {
      console.error('Failed to save personnel injury report', err);
      setInjuryFeedback(err.response?.data?.message || 'Failed to save personnel injury report.');
    } finally {
      setSavingInjury(false);
    }
  };

  const saveInjuryTemplate = async () => {
    if (!canManageInjuryTemplate) return;
    setSavingInjuryTemplate(true);
    try {
      const payload = {
        email: user.email,
        location: effectiveLocation,
        reportType: 'personnel_injury',
        classification: injuryForm.classification,
        maintenanceGroup: injuryForm.maintenanceGroup,
        checklistNumber: injuryForm.checklistNumber,
        checklistTitle: injuryForm.checklistTitle,
        updatedLabel: injuryForm.updatedLabel,
        checklistRows: injuryForm.checklistRows,
      };
      const res = await api.post('/save-safety-form-template', payload);
      const nextTemplate = { ...PERSONNEL_INJURY_TEMPLATE_FALLBACK, ...(res.data.template || {}) };
      setInjuryTemplate(nextTemplate);
      setInjuryTemplateFeedback(res.data.message || 'Personnel injury template saved.');
    } catch (err) {
      console.error('Failed to save personnel injury template', err);
      setInjuryTemplateFeedback(err.response?.data?.message || 'Failed to save personnel injury template.');
    } finally {
      setSavingInjuryTemplate(false);
    }
  };

  const exportInjuryReportToPDF = (report) => {
    const doc = new jsPDF('p', 'mm', 'letter');
    let y = 12;

    doc.setFontSize(14);
    doc.text('#505 PERSONNEL INJURY', 14, y);
    y += 8;
    doc.setFontSize(10);
    [
      `Controller: ${report.controllerName || '--'} (${report.controllerEmpNum || '--'})`,
      `Notified By: ${report.notifiedByName || '--'} (${report.notifiedByEmpNum || '--'})`,
      `Date/Time: ${formatDateTimeDisplay(report.reportDateTime)}`,
      `Injured 1: ${report.injuredRank || '--'} ${report.injuredName || '--'} / ${report.injuredWorkcenter || '--'}`,
      report.injured2Name ? `Injured 2: ${report.injured2Rank || '--'} ${report.injured2Name || '--'} / ${report.injured2Workcenter || '--'}` : null,
      `Location / Aircraft: ${report.locationAircraft || '--'}`,
      `Injury Type: ${report.injuryType || '--'}   Body Part: ${report.bodyPart || '--'}   Severity: ${report.severity || '--'}`,
      `Medical Care: ${report.medicalCare ? 'Yes' : 'No'}   Ambulance: ${report.ambulanceRequired ? 'Yes' : 'No'}   911: ${report.called911 ? 'Yes' : 'No'}`,
      `Head Injury: ${report.headInjury ? 'Yes' : 'No'}   Commander Escalation: ${report.commanderEscalationRequired ? 'Required' : 'No'}   Escorted Hospital Eval: ${report.escortedHospitalRequired ? 'Required' : 'No'}`,
    ].filter(Boolean).forEach(line => {
      doc.text(line, 14, y);
      y += 6;
    });

    y += 2;
    doc.setFontSize(10);
    doc.text('Circumstances:', 14, y);
    y += 5;
    const circumstanceLines = doc.splitTextToSize(report.circumstances || '--', 180);
    doc.text(circumstanceLines, 14, y);
    y += circumstanceLines.length * 5 + 4;

    doc.setFontSize(11);
    doc.text('Notification Checklist', 14, y);
    y += 6;
    doc.setFontSize(9);
    (report.checklistRows || []).forEach(row => {
      if (y > 260) {
        doc.addPage();
        y = 14;
      }
      const line = `${row.contact || '--'} | ${row.phone || '--'} | ${row.personNotified || '--'} | ${row.time || '--'}`;
      doc.text(line, 14, y);
      y += 5;
    });

    doc.save(`personnel_injury_${report.id || 'report'}.pdf`);
  };

  return (
    <div className="safety-reports">
      <div className="reports-panel safety-panel--uxo-form">
        <div className="reports-panel__header">
          <h3>9-Line UXO Report</h3>
        </div>
        {uxoFeedback ? <div className="safety-feedback">{uxoFeedback}</div> : null}
        <div className="safety-grid safety-grid--uxo">
          {[...Array(9)].map((_, index) => (
            <label key={`uxo-line-${index + 1}`}>
              <span>Line {index + 1}</span>
              <textarea
                rows={2}
                value={uxoForm[`line${index + 1}`]}
                onChange={event => setUxoForm(current => ({ ...current, [`line${index + 1}`]: event.target.value }))}
              />
            </label>
          ))}
        </div>
        <div className="safety-actions safety-actions--primary-row safety-actions--uxo">
          <button type="button" onClick={handleSubmitUxo} disabled={savingUxo}>
            {savingUxo ? 'Submitting...' : 'Submit UXO Report'}
          </button>
          <button type="button" onClick={() => setUxoForm(UXO_INITIAL_FORM)}>Clear UXO Form</button>
        </div>
      </div>

      <div className="reports-panel safety-panel--uxo-history">
        <div className="reports-panel__header">
          <h3>Previous UXO Reports</h3>
        </div>
        <div className="safety-filter-row safety-filter-row--uxo">
          <label>
            <span>Submitted Date</span>
            <input type="date" value={uxoFilterDate} onChange={event => setUxoFilterDate(event.target.value)} />
          </label>
          <label className="safety-filter-row__search">
            <span>Search</span>
            <input value={uxoSearchTerm} onChange={event => setUxoSearchTerm(event.target.value)} placeholder="Search lines 1-9" />
          </label>
        </div>
        <div className="reports-table-shell">
          <table className="reports-table">
            <thead>
              <tr>
                <th>Submitted At</th>
                {[...Array(9)].map((_, index) => (
                  <th key={`uxo-head-${index + 1}`}>Line {index + 1}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {uxoReports.map(report => (
                <tr key={report.id || `${report.submitted_at}-${report.line1}`}>
                  <td>{report.submitted_at || '--'}</td>
                  {[...Array(9)].map((_, index) => (
                    <td key={`uxo-cell-${index + 1}`}>{report[`line${index + 1}`] || '--'}</td>
                  ))}
                </tr>
              ))}
              {uxoReports.length === 0 && (
                <tr>
                  <td colSpan={10}>No UXO reports found for the current filters.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="reports-panel safety-panel--injury-form">
        <div className="reports-panel__header">
          <h3>#505 Personnel Injury</h3>
        </div>

        {injuryFeedback ? <div className="safety-feedback">{injuryFeedback}</div> : null}
        {headInjuryEffective ? (
          <div className="safety-warning">
            Head injury selected. Commander escalation and an escorted hospital evaluation are required automatically.
          </div>
        ) : null}
        {injuryForm.ambulanceRequired ? (
          <div className="safety-warning">
            Ambulance required. Group leadership contacts marked ambulance-only should be notified directly in addition to the checklist.
          </div>
        ) : null}

        <div className="safety-static-header">
          <div className="safety-static-card">
            <label>Classification</label>
            <strong>{injuryForm.classification || 'CUI'}</strong>
          </div>
          <div className="safety-static-card">
            <label>Maintenance Group</label>
            <strong>{injuryForm.maintenanceGroup || '--'}</strong>
          </div>
          <div className="safety-static-card safety-static-card--accent">
            <label>Checklist</label>
            <strong>{injuryForm.checklistNumber || '#505'}</strong>
            <span>{injuryForm.checklistTitle || 'PERSONNEL INJURY'}</span>
          </div>
          <div className="safety-static-card">
            <label>Updated</label>
            <strong>{injuryForm.updatedLabel || '--'}</strong>
          </div>
        </div>

        {canManageInjuryTemplate ? (
          <div className="safety-template-toolbar">
            <label>
              <span>Base Template Maintenance Group</span>
              <input value={injuryForm.maintenanceGroup} onChange={event => setInjuryForm({ ...injuryForm, maintenanceGroup: event.target.value })} />
            </label>
            <label>
              <span>Template Updated Label</span>
              <input value={injuryForm.updatedLabel} onChange={event => setInjuryForm({ ...injuryForm, updatedLabel: event.target.value })} />
            </label>
            <button type="button" className="safety-template-toolbar__button" onClick={saveInjuryTemplate} disabled={savingInjuryTemplate}>
              {savingInjuryTemplate ? 'Saving Template...' : `Save ${effectiveLocation || 'Base'} Template`}
            </button>
            {injuryTemplateFeedback ? <span className="safety-template-toolbar__feedback">{injuryTemplateFeedback}</span> : null}
          </div>
        ) : null}

        <div className="safety-grid safety-grid--injury-top">
          <div className="safety-panel-stack">
            <div className="safety-subpanel safety-subpanel--compact-form">
              <h4>Controller</h4>
              <PersonnelLookupAssist title="Controller" prefix="controller" form={injuryForm} setForm={setInjuryForm} personnelOptions={personnelOptions} />
              <label><span>Name</span><input value={injuryForm.controllerName} onChange={event => setInjuryForm({ ...injuryForm, controllerName: event.target.value })} /></label>
              <label><span>Personnel #</span><input value={injuryForm.controllerEmpNum} onChange={event => setInjuryForm({ ...injuryForm, controllerEmpNum: event.target.value })} /></label>
            </div>

            <div className="safety-subpanel safety-subpanel--compact-form">
              <h4>Notified By</h4>
              <PersonnelLookupAssist title="Notified By" prefix="notifiedBy" form={injuryForm} setForm={setInjuryForm} personnelOptions={personnelOptions} />
              <label><span>Name</span><input value={injuryForm.notifiedByName} onChange={event => setInjuryForm({ ...injuryForm, notifiedByName: event.target.value })} /></label>
              <label><span>Personnel #</span><input value={injuryForm.notifiedByEmpNum} onChange={event => setInjuryForm({ ...injuryForm, notifiedByEmpNum: event.target.value })} /></label>
            </div>
          </div>

          <div className="safety-panel-stack">
            <div className="safety-subpanel">
              <h4>Event Details</h4>
              <div className="safety-grid safety-grid--event-details">
                <label><span>Date / Time</span><input type="datetime-local" value={injuryForm.reportDateTime} onChange={event => setInjuryForm({ ...injuryForm, reportDateTime: event.target.value })} /></label>
                <label><span>Location / Aircraft</span><input value={injuryForm.locationAircraft} onChange={event => setInjuryForm({ ...injuryForm, locationAircraft: event.target.value })} /></label>
                <label><span>Injury Type</span><input value={injuryForm.injuryType} onChange={event => setInjuryForm({ ...injuryForm, injuryType: event.target.value })} placeholder="Crush / cut / slip / struck by" /></label>
                <label><span>Body Part</span><input value={injuryForm.bodyPart} onChange={event => setInjuryForm({ ...injuryForm, bodyPart: event.target.value })} placeholder="Ankle / hand / head" /></label>
                <label><span>Severity</span>
                  <select value={injuryForm.severity} onChange={event => setInjuryForm({ ...injuryForm, severity: event.target.value })}>
                    <option value="Low">Low</option>
                    <option value="Moderate">Moderate</option>
                    <option value="High">High</option>
                  </select>
                </label>
              </div>
            </div>

            <div className="safety-subpanel">
              <h4>Injured Member</h4>
              <div className="safety-grid safety-grid--two">
                <div className="safety-subpanel safety-subpanel--compact-form">
                  <h4>Member 1</h4>
                  <PersonnelLookupAssist title="Injured Member 1" prefix="injured" form={injuryForm} setForm={setInjuryForm} personnelOptions={personnelOptions} includeRank includeWorkcenter />
                  <label><span>Name (First, Last)</span><input value={injuryForm.injuredName} onChange={event => setInjuryForm({ ...injuryForm, injuredName: event.target.value })} placeholder="John Miller" /></label>
                  <label><span>Personnel #</span><input value={injuryForm.injuredEmpNum} onChange={event => setInjuryForm({ ...injuryForm, injuredEmpNum: event.target.value })} /></label>
                  <label><span>Rank</span><input value={injuryForm.injuredRank} onChange={event => setInjuryForm({ ...injuryForm, injuredRank: event.target.value })} /></label>
                  <label><span>Workcenter / Shop</span><input value={injuryForm.injuredWorkcenter} onChange={event => setInjuryForm({ ...injuryForm, injuredWorkcenter: event.target.value })} /></label>
                </div>
                <div className="safety-subpanel safety-subpanel--compact-form">
                  <h4>Member 2</h4>
                  <PersonnelLookupAssist title="Injured Member 2" prefix="injured2" form={injuryForm} setForm={setInjuryForm} personnelOptions={personnelOptions} includeRank includeWorkcenter />
                  <label><span>Name (First, Last)</span><input value={injuryForm.injured2Name} onChange={event => setInjuryForm({ ...injuryForm, injured2Name: event.target.value })} placeholder="John Miller" /></label>
                  <label><span>Personnel #</span><input value={injuryForm.injured2EmpNum} onChange={event => setInjuryForm({ ...injuryForm, injured2EmpNum: event.target.value })} /></label>
                  <label><span>Rank</span><input value={injuryForm.injured2Rank} onChange={event => setInjuryForm({ ...injuryForm, injured2Rank: event.target.value })} /></label>
                  <label><span>Workcenter / Shop</span><input value={injuryForm.injured2Workcenter} onChange={event => setInjuryForm({ ...injuryForm, injured2Workcenter: event.target.value })} /></label>
                </div>
              </div>
            </div>
          </div>

          <div className="safety-subpanel">
            <h4>Injury Details</h4>
            <div className="safety-grid safety-grid--injury-flags">
              <label className="safety-field--short"><span>Medical Care</span>
                <select value={injuryForm.medicalCare ? 'Yes' : 'No'} onChange={event => setInjuryForm(current => {
                  const nextMedicalCare = event.target.value === 'Yes';
                  return {
                    ...current,
                    medicalCare: nextMedicalCare,
                    ambulanceRequired: nextMedicalCare ? current.ambulanceRequired : false,
                    called911: nextMedicalCare ? current.called911 : false,
                  };
                })}>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </label>
              <label className="safety-field--short"><span>Ambulance</span>
                <select value={injuryForm.ambulanceRequired ? 'Yes' : 'No'} disabled={!medicalCareEffective} onChange={event => setInjuryForm({ ...injuryForm, ambulanceRequired: event.target.value === 'Yes' })}>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </label>
              <label className="safety-field--short"><span>911 Called</span>
                <select value={injuryForm.called911 ? 'Yes' : 'No'} disabled={!medicalCareEffective} onChange={event => setInjuryForm({ ...injuryForm, called911: event.target.value === 'Yes' })}>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </label>
              <label className="safety-field--short"><span>Head Injury</span>
                <select value={headInjuryEffective ? 'Yes' : 'No'} onChange={event => setInjuryForm({ ...injuryForm, headInjury: event.target.value === 'Yes' })}>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </label>
            </div>
            <div className="safety-grid safety-grid--two">
              <label className="safety-grid__wide">
                <span>Circumstances Leading to Injury</span>
                <textarea
                  rows={5}
                  value={injuryForm.circumstances}
                  onChange={event => setInjuryForm({ ...injuryForm, circumstances: event.target.value })}
                  placeholder="Describe what happened. For significant-events wording, do not include the injured member's name."
                />
              </label>
              <label className="safety-grid__wide"><span>Notes / Follow-up</span><textarea rows={3} value={injuryForm.notes} onChange={event => setInjuryForm({ ...injuryForm, notes: event.target.value })} /></label>
            </div>
            <div className="safety-subpanel safety-subpanel--compact">
              <h4>Automatic Requirements</h4>
              <div className="safety-flag-list">
                <div><strong>Commander Escalation</strong><span>{headInjuryEffective ? 'Required' : 'Not automatically required'}</span></div>
                <div><strong>Escorted Hospital Evaluation</strong><span>{headInjuryEffective ? 'Required' : 'Not automatically required'}</span></div>
              </div>
            </div>
          </div>
        </div>

        <div className="safety-actions safety-actions--details-row">
          <button type="button" onClick={saveInjuryReport} disabled={savingInjury}>
            {savingInjury ? 'Saving...' : injuryForm.id ? 'Update Personnel Injury Report' : 'Submit Personnel Injury Report'}
          </button>
          <button type="button" onClick={resetInjuryForm}>Clear Form</button>
          {injuryForm.id ? <button type="button" onClick={() => exportInjuryReportToPDF(injuryForm)}>Export Current Report</button> : null}
        </div>

        <div className="safety-subpanel">
          <h4>Checklist Notes</h4>
          <ul className="safety-note-list">
            <li>Notify group leadership via email for all injuries.</li>
            <li>If an ambulance is required, call group leadership directly in addition to sending the checklist.</li>
            <li>If personnel require 911, confirm whether the call has been made and whether it is exercise or real world.</li>
          </ul>
        </div>

        <div className="safety-subpanel">
          <h4>Notification Checklist</h4>
          <div className="safety-checklist-grid">
            <div className="reports-table-shell">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>Contact</th>
                    <th>Phone / Note</th>
                    <th>Person Notified</th>
                    <th>Time</th>
                    {canManageInjuryTemplate ? <th>Template</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {injuryChecklistLeft.map((row, index) => (
                    <React.Fragment key={`${row.contact}-${index}`}>
                      <tr>
                        <td>
                          {canManageInjuryTemplate ? (
                            <input value={row.contact || ''} onChange={event => updateInjuryChecklistRow(index, 'contact', event.target.value)} placeholder="Contact" />
                          ) : row.contact}
                        </td>
                        <td>
                          {canManageInjuryTemplate ? (
                            <input value={row.phone || ''} onChange={event => updateInjuryChecklistRow(index, 'phone', event.target.value)} placeholder="Phone / routing" />
                          ) : (
                            <>
                              <div>{row.phone}</div>
                              {row.note ? <div className="safety-table-note">{row.note}</div> : null}
                            </>
                          )}
                        </td>
                        <td><input value={row.personNotified || ''} onChange={event => updateInjuryChecklistRow(index, 'personNotified', event.target.value)} /></td>
                        <td><input value={row.time || ''} onChange={event => updateInjuryChecklistRow(index, 'time', event.target.value)} placeholder="HHMM / HH:MM" /></td>
                        {canManageInjuryTemplate ? (
                          <td><button type="button" className="safety-table-action" onClick={() => removeInjuryChecklistRow(index)}>Remove</button></td>
                        ) : null}
                      </tr>
                      {canManageInjuryTemplate ? (
                        <tr className="safety-table-note-row">
                          <td colSpan={3}>
                            <input
                              className="safety-checklist-note-input"
                              value={row.note || ''}
                              onChange={event => updateInjuryChecklistRow(index, 'note', event.target.value)}
                              placeholder="Optional note"
                            />
                          </td>
                          <td colSpan={canManageInjuryTemplate ? 2 : 1}></td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="reports-table-shell">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>Contact</th>
                    <th>Phone / Note</th>
                    <th>Person Notified</th>
                    <th>Time</th>
                    {canManageInjuryTemplate ? <th>Template</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {injuryChecklistRight.map((row, index) => {
                    const rowIndex = injuryChecklistMidpoint + index;
                    return (
                      <React.Fragment key={`${row.contact}-${rowIndex}`}>
                        <tr>
                          <td>
                            {canManageInjuryTemplate ? (
                              <input value={row.contact || ''} onChange={event => updateInjuryChecklistRow(rowIndex, 'contact', event.target.value)} placeholder="Contact" />
                            ) : row.contact}
                          </td>
                          <td>
                            {canManageInjuryTemplate ? (
                              <input value={row.phone || ''} onChange={event => updateInjuryChecklistRow(rowIndex, 'phone', event.target.value)} placeholder="Phone / routing" />
                            ) : (
                              <>
                                <div>{row.phone}</div>
                                {row.note ? <div className="safety-table-note">{row.note}</div> : null}
                              </>
                            )}
                          </td>
                          <td><input value={row.personNotified || ''} onChange={event => updateInjuryChecklistRow(rowIndex, 'personNotified', event.target.value)} /></td>
                          <td><input value={row.time || ''} onChange={event => updateInjuryChecklistRow(rowIndex, 'time', event.target.value)} placeholder="HHMM / HH:MM" /></td>
                          {canManageInjuryTemplate ? (
                            <td><button type="button" className="safety-table-action" onClick={() => removeInjuryChecklistRow(rowIndex)}>Remove</button></td>
                          ) : null}
                        </tr>
                        {canManageInjuryTemplate ? (
                          <tr className="safety-table-note-row">
                            <td colSpan={3}>
                              <input
                                className="safety-checklist-note-input"
                                value={row.note || ''}
                                onChange={event => updateInjuryChecklistRow(rowIndex, 'note', event.target.value)}
                                placeholder="Optional note"
                              />
                            </td>
                            <td colSpan={canManageInjuryTemplate ? 2 : 1}></td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {canManageInjuryTemplate ? (
            <div className="safety-actions safety-actions--inline">
              <button type="button" onClick={addInjuryChecklistRow}>Add Template Contact Row</button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="reports-panel safety-panel--injury-history">
        <div className="reports-panel__header">
          <h3>Previous Personnel Injury Reports</h3>
          <span>Privacy-reduced list view with full details available on open</span>
        </div>
        <div className="safety-filter-row">
          <label>
            <span>Submitted Date</span>
            <input type="date" value={injuryFilterDate} onChange={event => setInjuryFilterDate(event.target.value)} />
          </label>
          <label className="safety-filter-row__search">
            <span>Search</span>
            <input value={injurySearchTerm} onChange={event => setInjurySearchTerm(event.target.value)} placeholder="Workcenter, injury type, body part, severity" />
          </label>
        </div>
        <div className="reports-table-shell">
          <table className="reports-table">
            <thead>
              <tr>
                <th>Date / Time</th>
                <th>Workcenter</th>
                <th>Location / Aircraft</th>
                <th>Injury Type</th>
                <th>Body Part</th>
                <th>Severity</th>
                <th>Medical Care</th>
                <th>Head Injury</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {injuryReports.map(report => (
                <tr key={report.id}>
                  <td>{formatDateTimeDisplay(report.reportDateTime)}</td>
                  <td>{report.injuredWorkcenter || '--'}</td>
                  <td>{report.locationAircraft || '--'}</td>
                  <td>{report.injuryType || '--'}</td>
                  <td>{report.bodyPart || '--'}</td>
                  <td>{report.severity || '--'}</td>
                  <td>{report.medicalCare ? 'Yes' : 'No'}</td>
                  <td>{report.headInjury ? 'Yes' : 'No'}</td>
                  <td className="status-table__actions">
                    <button type="button" onClick={() => loadInjuryIntoForm(report)}>Open</button>
                    <button type="button" onClick={() => exportInjuryReportToPDF(report)}>PDF</button>
                  </td>
                </tr>
              ))}
              {injuryReports.length === 0 && (
                <tr>
                  <td colSpan={9}>No personnel injury reports found for the current filters.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
